import React from "react";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { ElementsConsumer, PaymentElement, Elements } from "@stripe/react-stripe-js";
import { withTranslation } from "react-i18next";

const stripePromise = loadStripe("pk_test_s2DqVxCx4xKdJBPVDmihJv0s");

// Stripe's payment form in the account pages' compact look (the Scenes tab's tokens, see account/account.css)
const APPEARANCE = {
  theme: "stripe",
  variables: {
    colorPrimary: "#1355b2",
    colorText: "#063e6c",
    colorTextSecondary: "#54667e",
    colorDanger: "#d32f2f",
    fontFamily: '"Open Sans", sans-serif',
    fontSizeBase: "13px",
    spacingUnit: "3px",
    borderRadius: "4px",
  },
  rules: {
    ".Input": { border: "1px solid #dbe3ee", boxShadow: "none", padding: "6px 8px" },
    ".Input:focus": { borderColor: "#1355b2", boxShadow: "0 0 0 1px #1355b2" },
    ".Label": { fontWeight: "600", color: "#063e6c" },
    ".Tab": { border: "1px solid #dbe3ee", boxShadow: "none" },
    ".Tab--selected": { borderColor: "#b3cff6", backgroundColor: "#e1eeff", color: "#1355b2" },
  },
};
// dark mode: the same look in the design's dark palette (account.css [data-vvt="dark"])
const DARK_APPEARANCE = {
  theme: "night",
  variables: {
    ...APPEARANCE.variables,
    colorPrimary: "#86b4ff",
    colorBackground: "#151f2c",
    colorText: "#e4ecf7",
    colorTextSecondary: "#9db0c7",
    colorTextPlaceholder: "#8fa3bd",
    colorDanger: "#f44336",
  },
  rules: {
    ".Input": { border: "1px solid #2a3a4f", boxShadow: "none", padding: "6px 8px" },
    ".Input:focus": { borderColor: "#86b4ff", boxShadow: "0 0 0 1px #86b4ff" },
    ".Label": { fontWeight: "600", color: "#e4ecf7" },
    ".Tab": { border: "1px solid #2a3a4f", boxShadow: "none" },
    ".Tab--selected": { borderColor: "#36557f", backgroundColor: "#1d3150", color: "#86b4ff" },
  },
};
const FONTS = [{ cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans:400,600&display=swap" }];

class CheckoutForm extends React.Component {
  state = { submitting: false, errorMessage: "" };

  handleSubmit = async (event) => {
    event.preventDefault();
    const { stripe, elements } = this.props;
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    this.setState({ submitting: true, errorMessage: "" });
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: process.env.REACT_APP_APP_BASE + "/account/save_payment",
      },
    });

    // on success Stripe redirects to return_url; otherwise show why (e.g. incomplete card details)
    if (result.error) {
      this.setState({ submitting: false, errorMessage: result.error.message });
    }
  };

  render() {
    const { t } = this.props;
    return (
      <form onSubmit={this.handleSubmit}>
        <div className="ac-body" style={{ gap: "12px" }}>
          <PaymentElement />
          {this.state.errorMessage !== "" && <Alert severity="error">{this.state.errorMessage}</Alert>}
        </div>
        <div className="ac-foot">
          <Button className="Button ac-btn" component={Link} to="/account/billing">
            {t("Cancel")}
          </Button>
          <Button type="submit" className="Button ButtonPrimary ac-btn" disabled={!this.props.stripe || this.state.submitting}>
            {t("Save")}
          </Button>
        </div>
      </form>
    );
  }
}

class PaymentForm extends React.Component {
  render() {
    if (!this.props.secret) {
      return (
        <div className="ac-body">
          <CircularProgress size={24} />
        </div>
      );
    }
    // Elements applies a changed appearance in place, so the form follows the dark mode switch
    const appearance = this.props.dark ? DARK_APPEARANCE : APPEARANCE;
    return (
      <Elements stripe={stripePromise} options={{ clientSecret: this.props.secret, appearance, fonts: FONTS }}>
        <ElementsConsumer>{({ stripe, elements }) => <CheckoutForm stripe={stripe} elements={elements} t={this.props.t} />}</ElementsConsumer>
      </Elements>
    );
  }
}

// dark when the MUI theme is (App switches it with the page's dark mode)
function ThemedPaymentForm(props) {
  return <PaymentForm {...props} dark={useTheme().palette.mode === "dark"} />;
}

export default withTranslation()(ThemedPaymentForm);
